import { useEffect, useMemo, useState, useCallback, useRef, startTransition } from 'react';
import type { DataGridProps, SelectTabData, SelectTabEvent } from '@fluentui/react-components';
import { useApiRequest } from './useApiRequest';
import { useMsal } from '@azure/msal-react';
import { ApiService } from '../services/apiService';
import { getFriendlyApiErrorMessage, logApiError } from '../services/apiErrorMessages';
import { normalizeStatus, formatNoteText } from '../utils/formatters';
import { exportToCsv } from '../utils/exportToCsv';
import { usePartnerAliases } from '../contexts/PartnerAliasContext';
import { PAGE_SIZE } from '../constants/table';
import type { PartnerReviewConfig, PartnerReviewItem, PartnerEligibilityResponse, TierChangeRecord, Note, ChangeHistory } from '../models/partnerReview';
import type { NotesEditorHandle } from '../components/NotesEditor';

export function usePartnerReview(config: PartnerReviewConfig) {
  const { data, isLoading, error, execute } = useApiRequest<PartnerEligibilityResponse>();
  const { instance, accounts } = useMsal();
  const { getAlias, getDisplayName } = usePartnerAliases();

  const [submittingMpnIds, setSubmittingMpnIds] = useState<Set<string>>(new Set());
  const [actionError, setActionError] = useState<string | null>(null);
  const [changeOverrides, setChangeOverrides] = useState<Map<string, ChangeHistory | null>>(new Map());
  const [gridSelectionKey, setGridSelectionKey] = useState(0);
  const [selectedCount, setSelectedCount] = useState(0);
  const selectedRowsRef = useRef<Set<string | number>>(new Set());

  const [searchQuery, setSearchQuery] = useState('');
  const [filterCurrentTier, setFilterCurrentTier] = useState('All');
  const [filterNextTier, setFilterNextTier] = useState('All');
  const [filterLocation, setFilterLocation] = useState('All');
  const [filterStatus, setFilterStatus] = useState('All');
  const [currentPage, setCurrentPage] = useState(1);

  const [selectedPartner, setSelectedPartner] = useState<PartnerReviewItem | null>(null);
  const selectedPartnerRef = useRef<PartnerReviewItem | null>(null);
  useEffect(() => { selectedPartnerRef.current = selectedPartner; }, [selectedPartner]);
  const [drawerTab, setDrawerTab] = useState<string>('overview');

  const { data: tierChanges, isLoading: isLoadingHistory, execute: executeHistory } = useApiRequest<TierChangeRecord[]>();
  const [initialNoteBody, setInitialNoteBody] = useState('');
  const [initialNoteSystemPrefix, setInitialNoteSystemPrefix] = useState<string | null>(null);
  const notesEditorRef = useRef<NotesEditorHandle>(null);
  const { data: allNotes, isLoading: isLoadingNotes, execute: executeLoadNotes } = useApiRequest<Note[]>();
  const [savedNotesMpnIds, setSavedNotesMpnIds] = useState<Set<string>>(new Set());
  // Tracks the last-known note body per mpnId — used to decide whether to keep "View note" on reset.
  const sessionNoteBodiesRef = useRef<Map<string, string>>(new Map());

  const [sortState, setSortState] = useState<{ sortColumn: string; sortDirection: 'ascending' | 'descending' }>({
    sortColumn: 'partnerName',
    sortDirection: 'ascending',
  });

  const refresh = useCallback(() => {
    execute((api) => api.get<PartnerEligibilityResponse>(config.fetchEndpoint));
  }, [execute, config.fetchEndpoint]);

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    notesEditorRef.current?.flush();
    const locationMpnId = selectedPartner?.locationMpnId;
    if (!locationMpnId) return;

    executeHistory((api) => api.get<TierChangeRecord[]>(`/TierChange/${locationMpnId}`));
    setInitialNoteBody('');
    setInitialNoteSystemPrefix(null);
    executeLoadNotes(async (api) => {
      const notes = await api.get<Note[]>(`/Notes/${locationMpnId}`);
      if (notes.length > 0) {
        setInitialNoteBody(notes[0].body);
        setInitialNoteSystemPrefix(notes[0].systemPrefix ?? null);
        sessionNoteBodiesRef.current.set(locationMpnId, notes[0].body ?? '');
      } else {
        sessionNoteBodiesRef.current.set(locationMpnId, '');
      }
      return notes;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedPartner]);

  const partners = useMemo(() => {
    const raw = data?.partners ?? [];
    return raw.map((p) => {
      let updated = p;
      if (p.locationMpnId && savedNotesMpnIds.has(p.locationMpnId) && !p.hasNotes) {
        updated = { ...updated, hasNotes: true };
      }
      if (p.locationMpnId && changeOverrides.has(p.locationMpnId)) {
        updated = { ...updated, change: changeOverrides.get(p.locationMpnId) ?? null };
      }
      return updated;
    });
  }, [data, savedNotesMpnIds, changeOverrides]);

  const { candidates, pending, approved, denied } = useMemo(() => ({
    candidates: partners.length,
    pending: partners.filter((p) => normalizeStatus(p.change?.status) === 'Pending').length,
    approved: partners.filter((p) => normalizeStatus(p.change?.status) === 'Approved').length,
    denied: partners.filter((p) => normalizeStatus(p.change?.status) === 'Denied').length,
  }), [partners]);

  const locationOptions = useMemo(() =>
    [...new Set(partners.map((p) => p.contractLocation).filter(Boolean) as string[])].sort(),
    [partners],
  );

  const lastUpdatedAt = useMemo(() => {
    const times = partners
      .map((p) => p.change?.timestamp ? new Date(p.change.timestamp).getTime() : NaN)
      .filter((t) => !isNaN(t));
    if (times.length === 0) return null;
    return new Date(Math.max(...times)).toISOString();
  }, [partners]);

  const filteredPartners = useMemo(() => {
    return partners.filter((p) => {
      if (filterCurrentTier !== 'All' && p.currentTier !== filterCurrentTier) return false;
      if (filterNextTier !== 'All' && p.nextTier !== filterNextTier) return false;
      if (filterLocation !== 'All' && p.contractLocation !== filterLocation) return false;
      if (filterStatus !== 'All') {
        const status = normalizeStatus(p.change?.status);
        if (status !== filterStatus) return false;
      }
      if (searchQuery) {
        const q = searchQuery.toLowerCase();
        return (
          (p.partnerName?.toLowerCase().indexOf(q) ?? -1) !== -1 ||
          (getAlias(p.partnerName)?.toLowerCase().indexOf(q) ?? -1) !== -1 ||
          String(p.tpId).indexOf(q) !== -1 ||
          (p.globalMpnId ?? '').indexOf(q) !== -1
        );
      }
      return true;
    });
  }, [partners, filterCurrentTier, filterNextTier, filterLocation, filterStatus, searchQuery, getAlias]);

  useEffect(() => setCurrentPage(1), [filterCurrentTier, filterNextTier, filterLocation, filterStatus, searchQuery]);

  useEffect(() => {
    selectedRowsRef.current = new Set();
    setSelectedCount(0);
  }, [filteredPartners]);

  // Columns are defined in PartnerReviewGrid; sorting uses column IDs
  const sortedPartners = useMemo(() => {
    const comparators: Record<string, (a: PartnerReviewItem, b: PartnerReviewItem) => number> = {
      partnerName: (a, b) => (a.partnerName ?? '').localeCompare(b.partnerName ?? ''),
      contractLocation: (a, b) => (a.contractLocation ?? '').localeCompare(b.contractLocation ?? ''),
      contractType: (a, b) => (a.contractType ?? '').localeCompare(b.contractType ?? ''),
      globalMpnId: (a, b) => (a.globalMpnId ?? '').localeCompare(b.globalMpnId ?? ''),
      tpId: (a, b) => a.tpId - b.tpId,
      currentTier: (a, b) => (a.currentTier ?? '').localeCompare(b.currentTier ?? ''),
      ttmRevenue: (a, b) => (a.ttmRevenue ?? 0) - (b.ttmRevenue ?? 0),
      nextTtm: (a, b) => (a.nextTtm ?? 0) - (b.nextTtm ?? 0),
      futureTtm: (a, b) => (a.futureTtm ?? 0) - (b.futureTtm ?? 0),
      nextTier: (a, b) => a.nextTier.localeCompare(b.nextTier),
      status: (a, b) => (normalizeStatus(a.change?.status) ?? '').localeCompare(normalizeStatus(b.change?.status) ?? ''),
    };
    const cmp = comparators[sortState.sortColumn];
    if (!cmp) return filteredPartners;
    const sorted = [...filteredPartners].sort(cmp);
    return sortState.sortDirection === 'descending' ? sorted.reverse() : sorted;
  }, [filteredPartners, sortState]);

  const totalItems = sortedPartners.length;
  const totalPages = Math.max(1, Math.ceil(totalItems / PAGE_SIZE));
  const startIndex = (currentPage - 1) * PAGE_SIZE;
  const endIndex = Math.min(startIndex + PAGE_SIZE, totalItems);
  const paginatedPartners = sortedPartners.slice(startIndex, endIndex);

  const goToPage = (page: number) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  const pageOptions = useMemo(() => Array.from({ length: totalPages }, (_, i) => i + 1), [totalPages]);

  const onSelectionChange = useCallback<NonNullable<DataGridProps['onSelectionChange']>>(
    (_e, data) => {
      selectedRowsRef.current = data.selectedItems;
      startTransition(() => setSelectedCount(data.selectedItems.size));
    },
    [],
  );

  const onRowClickCapture = useCallback((e: React.MouseEvent) => {
    const target = e.target as HTMLElement;
    if (target.closest('[data-partner-link]')) return;
    if (target.closest('[data-action-button]')) return;
    if (!target.closest('[role="checkbox"]') && !target.matches('input[type="checkbox"]')) {
      e.stopPropagation();
    }
  }, []);

  const onPartnerClick = useCallback((partner: PartnerReviewItem) => {
    setDrawerTab('overview');
    setSelectedPartner(partner);
  }, []);

  const onCloseDrawer = useCallback(() => {
    notesEditorRef.current?.flush();
    setSelectedPartner(null);
  }, []);

  const buildPayload = (partner: PartnerReviewItem, status: string) => ({
    mpnId: partner.locationMpnId ?? '',
    previousTier: partner.currentTier ?? '',
    newTier: partner.nextTier,
    status,
    partnerName: partner.partnerName ?? '',
    contractLocation: partner.contractLocation ?? '',
    contractStatus: partner.contractType ?? '',
    globalMpnId: partner.globalMpnId ?? '',
    tpid: String(partner.tpId),
    ttmRevenue: partner.ttmRevenue,
    nextTtm: partner.nextTtm,
    futureTtm: partner.futureTtm,
  });

  const submitChanges = useCallback(async (targets: PartnerReviewItem[], status: 'Approved' | 'Denied') => {
    const valid = targets.filter((p) => p.locationMpnId != null);
    if (valid.length === 0) return;

    const account = instance.getActiveAccount() ?? accounts[0];
    if (!account) {
      setActionError('No active account found. Please sign in again.');
      return;
    }
    const api = new ApiService(instance, account);

    const mpnIds = new Set(valid.map((p) => p.locationMpnId!));
    setSubmittingMpnIds((prev) => new Set([...prev, ...mpnIds]));
    setActionError(null);

    try {
      const result = await api.post<TierChangeRecord[]>(config.submitEndpoint, {
        [config.requestItemsKey]: valid.map((p) => buildPayload(p, status)),
      });

      setChangeOverrides((prev) => {
        const next = new Map(prev);
        for (const saved of result) {
          const mpnId = valid.find((p) => p.locationMpnId === saved.mpnId)?.locationMpnId;
          if (mpnId) {
            next.set(mpnId, {
              previousTier: saved.previousTier,
              newTier: saved.newTier,
              status: saved.status,
              changedBy: saved.changedBy,
              timestamp: saved.timestamp,
            });
          }
        }
        return next;
      });

      // Approving writes a system-prefix note server-side; denying clears it (and deletes
      // the note outright when it has no body). Mark both so the grid reflects the write
      // immediately — the grid separately keeps the link visible for denied reviews.
      setSavedNotesMpnIds((prev) => new Set([...prev, ...mpnIds]));

      selectedRowsRef.current = new Set();
      setSelectedCount(0);
      setGridSelectionKey((k) => k + 1);
    } catch (err) {
      logApiError(config.submitEndpoint, err);
      setActionError(getFriendlyApiErrorMessage(err));
    } finally {
      setSubmittingMpnIds((prev) => {
        const next = new Set(prev);
        mpnIds.forEach((id) => next.delete(id));
        return next;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instance, accounts, config.submitEndpoint, config.requestItemsKey]);

  const onApprove = useCallback((partner: PartnerReviewItem) => submitChanges([partner], 'Approved'), [submitChanges]);
  const onDeny = useCallback((partner: PartnerReviewItem) => submitChanges([partner], 'Denied'), [submitChanges]);

  const onReset = useCallback(async (partner: PartnerReviewItem) => {
    const mpnId = partner.locationMpnId;
    if (!mpnId) return;

    const account = instance.getActiveAccount() ?? accounts[0];
    if (!account) {
      setActionError('No active account found. Please sign in again.');
      return;
    }
    const api = new ApiService(instance, account);

    setSubmittingMpnIds((prev) => new Set([...prev, mpnId]));
    setActionError(null);

    try {
      await api.delete(`/TierChange/${mpnId}`);

      // Set override to null — signals "explicitly reset this session", ensuring the UI shows
      // no active change even if the underlying API data is stale (e.g., loaded before this reset).
      setChangeOverrides((prev) => {
        const next = new Map(prev);
        next.set(mpnId, null);
        return next;
      });

      // Clear system prefix in the flyout and remove "View note" only if no body.
      notesEditorRef.current?.clearSystemPrefix();
      setInitialNoteSystemPrefix(null);
      setSavedNotesMpnIds((prev) => {
        const next = new Set(prev);
        const hasBody = (sessionNoteBodiesRef.current.get(mpnId) ?? '').trim().length > 0;
        if (!hasBody) next.delete(mpnId);
        return next;
      });

      // Reload tier change history if this partner's flyout is open.
      const openMpnId = selectedPartnerRef.current?.locationMpnId;
      if (openMpnId === mpnId) {
        executeHistory((api) => api.get<TierChangeRecord[]>(`/TierChange/${mpnId}`));
      }

      setGridSelectionKey((k) => k + 1);
    } catch (err) {
      logApiError(`/TierChange/${mpnId}`, err);
      setActionError(getFriendlyApiErrorMessage(err));
    } finally {
      setSubmittingMpnIds((prev) => {
        const next = new Set(prev);
        next.delete(mpnId);
        return next;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [instance, accounts]);

  const getSelectedPartners = useCallback(() => {
    const selectedIds = selectedRowsRef.current;
    return sortedPartners.filter((p) => selectedIds.has(`${p.locationMpnId ?? 'null'}-${p.tpId}`));
  }, [sortedPartners]);

  const onBulkApprove = useCallback(() => submitChanges(getSelectedPartners(), 'Approved'), [getSelectedPartners, submitChanges]);
  const onBulkDeny = useCallback(() => submitChanges(getSelectedPartners(), 'Denied'), [getSelectedPartners, submitChanges]);

  const onExportToCsv = useCallback(async () => {
    const account = instance.getActiveAccount() ?? accounts[0];
    if (!account) return;
    const api = new ApiService(instance, account);

    // Fetch notes in parallel for all partners that have them.
    const partnersWithNotes = sortedPartners.filter((p) => p.hasNotes && p.locationMpnId);
    const noteResults = await Promise.allSettled(
      partnersWithNotes.map((p) => api.get<Note[]>(`/Notes/${p.locationMpnId!}`))
    );
    const notesMap = new Map<string, string>();
    noteResults.forEach((result, i) => {
      if (result.status === 'fulfilled' && result.value.length > 0) {
        const note = result.value[0];
        notesMap.set(partnersWithNotes[i].locationMpnId!, formatNoteText(note.systemPrefix, note.body));
      }
    });

    exportToCsv<PartnerReviewItem>({
      fileName: config.exportFileName,
      columns: [
        { header: 'Partner name', accessor: (r) => getDisplayName(r.partnerName) ?? '' },
        { header: 'Contract location', accessor: (r) => r.contractLocation },
        { header: 'Contract status', accessor: (r) => r.contractType },
        { header: 'Global MPN ID', accessor: (r) => r.globalMpnId },
        { header: 'TPID', accessor: (r) => r.tpId },
        { header: 'Current tier', accessor: (r) => r.currentTier },
        { header: 'TTM revenue', accessor: (r) => r.ttmRevenue },
        { header: 'Next TTM (for Ops)', accessor: (r) => r.nextTtm },
        { header: 'Future TTM', accessor: (r) => r.futureTtm },
        { header: config.tierChangeLabel, accessor: (r) => r.nextTier },
        { header: 'Status', accessor: (r) => normalizeStatus(r.change?.status) },
        { header: 'Note', accessor: (r) => (r.locationMpnId ? (notesMap.get(r.locationMpnId) ?? '') : '') },
      ],
      rows: sortedPartners,
    });
  }, [sortedPartners, config, instance, accounts, getDisplayName]);

  const onNoteSaved = useCallback((mpnId: string, body: string) => {
    sessionNoteBodiesRef.current.set(mpnId, body);
    setSavedNotesMpnIds((prev) => {
      if (prev.has(mpnId)) return prev;
      const next = new Set(prev);
      next.add(mpnId);
      return next;
    });
  }, []);

  const loadNotes = useCallback(() => {
    if (!selectedPartner?.locationMpnId) return;
    executeLoadNotes((api) => api.get<Note[]>(`/Notes/${selectedPartner.locationMpnId}`));
  }, [selectedPartner, executeLoadNotes]);

  const onDrawerTabSelect = useCallback((_e: SelectTabEvent, data: SelectTabData) => {
    const tab = data.value as string;
    setDrawerTab(tab);
    if (tab === 'notesHistory') loadNotes();
  }, [loadNotes]);

  const onNotesHistoryLink = useCallback(() => {
    setDrawerTab('notesHistory');
    loadNotes();
  }, [loadNotes]);

  const selectedPartnerIndex = useMemo(() => {
    if (!selectedPartner) return -1;
    return sortedPartners.findIndex(
      (p) => p.locationMpnId === selectedPartner.locationMpnId && p.tpId === selectedPartner.tpId,
    );
  }, [selectedPartner, sortedPartners]);

  const navigatePartner = useCallback((direction: 'up' | 'down') => {
    const newIndex = direction === 'up' ? selectedPartnerIndex - 1 : selectedPartnerIndex + 1;
    if (newIndex < 0 || newIndex >= sortedPartners.length) return;
    const targetPage = Math.floor(newIndex / PAGE_SIZE) + 1;
    if (targetPage !== currentPage) setCurrentPage(targetPage);
    setDrawerTab('overview');
    setSelectedPartner(sortedPartners[newIndex]);
  }, [selectedPartnerIndex, sortedPartners, currentPage]);

  const defaultSortState = useMemo<DataGridProps['defaultSortState']>(
    () => ({ sortColumn: 'partnerName', sortDirection: 'ascending' }),
    [],
  );

  const ttmPeriodLabel = useMemo(() => {
    const now = new Date();
    const n = new Date(now.getFullYear(), now.getMonth(), 1);
    const start = new Date(n.getFullYear(), n.getMonth() - 13, 1);
    const end = new Date(n.getFullYear(), n.getMonth() - 2, 1);
    const fmt = (d: Date) => d.toLocaleString('en-US', { month: 'long', year: 'numeric' });
    return `${fmt(start)} – ${fmt(end)}${end.getMonth() === 5 ? ' (Fiscal year end)' : ''}`;
  }, []);

  return {
    // Data
    partners,
    isLoading,
    error,
    refresh,
    // Counts
    candidates,
    pending,
    approved,
    denied,
    // Filters
    searchQuery, setSearchQuery,
    filterCurrentTier, setFilterCurrentTier,
    filterNextTier, setFilterNextTier,
    filterLocation, setFilterLocation,
    filterStatus, setFilterStatus,
    locationOptions,
    lastUpdatedAt,
    ttmPeriodLabel,
    // Pagination
    currentPage,
    totalPages,
    totalItems,
    startIndex,
    endIndex,
    paginatedPartners,
    sortedPartners,
    goToPage,
    pageOptions,
    // Selection
    selectedCount,
    gridSelectionKey,
    onSelectionChange,
    onRowClickCapture,
    // Sort
    sortState,
    setSortState,
    defaultSortState,
    // Actions
    submittingMpnIds,
    actionError,
    onApprove,
    onDeny,
    onReset,
    onBulkApprove,
    onBulkDeny,
    onExportToCsv,
    // Drawer
    selectedPartner,
    onPartnerClick,
    onCloseDrawer,
    drawerTab,
    onDrawerTabSelect,
    selectedPartnerIndex,
    navigatePartner,
    // Notes & History
    tierChanges,
    isLoadingHistory,
    allNotes,
    isLoadingNotes,
    initialNoteBody,
    initialNoteSystemPrefix,
    onNoteSaved,
    onNotesHistoryLink,
    notesEditorRef,
    // Config
    config,
  };
}
